import { Component } from '@theme/component';

/**
 * Truncates collection description to two lines with a read-more toggle.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} content - The description content element
 * @property {HTMLButtonElement} toggle - The read more / show less button
 *
 * @extends {Component<Refs>}
 */
class CollectionDescriptionComponent extends Component {
  requiredRefs = ['content', 'toggle'];

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {AbortController} */
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    this.refs.toggle.addEventListener('click', this.#toggle, { signal });
    this.#resizeObserver = new ResizeObserver(() => this.#updateToggleVisibility());
    this.#resizeObserver.observe(this.refs.content);

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => this.#updateToggleVisibility());
    } else {
      this.#updateToggleVisibility();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#resizeObserver?.disconnect();
  }

  /**
   * Shows or hides the toggle based on whether the content overflows two lines.
   */
  #updateToggleVisibility() {
    const { content, toggle } = this.refs;
    const isExpanded = this.dataset.expanded === 'true';

    if (isExpanded) return;

    content.classList.add('collection-description__content--clamped');

    const needsToggle = content.scrollHeight > content.clientHeight + 1;

    toggle.classList.toggle('hidden', !needsToggle);
    toggle.setAttribute('aria-hidden', needsToggle ? 'false' : 'true');

    if (!needsToggle) {
      content.classList.remove('collection-description__content--clamped');
    }
  }

  #toggle = () => {
    const { content, toggle } = this.refs;
    const isExpanded = this.dataset.expanded === 'true';

    this.dataset.expanded = isExpanded ? 'false' : 'true';
    content.classList.toggle('collection-description__content--clamped', isExpanded);
    toggle.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');

    if (isExpanded) {
      requestAnimationFrame(() => this.#updateToggleVisibility());
    }
  };
}

if (!customElements.get('collection-description-component')) {
  customElements.define('collection-description-component', CollectionDescriptionComponent);
}
